import { useState, useCallback } from 'react'
import SearchForm from '../components/SearchForm'
import SearchResults from '../components/SearchResults'
import ErrorMessage from '../components/ErrorMessage'
import { searchService } from '../services/searchService'
import { SearchQuery, SearchResult } from '../types'
import './SearchCOAPage.css'

const PAGE_SIZE = 20

function sanitizeError(err: unknown): string {
  if (err instanceof Error) {
    if (
      err.message.includes('is not valid JSON') ||
      err.message.startsWith('Unexpected token')
    ) {
      return 'The search service is temporarily unavailable. Please try again later.'
    }
    if (err.message === 'Failed to fetch') {
      return 'Unable to connect to the search service. Please check your connection and try again.'
    }
    return err.message
  }
  return 'An unexpected error occurred. Please try again.'
}

function SearchCOAPage() {
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalResults, setTotalResults] = useState(0)
  const [lastQuery, setLastQuery] = useState<SearchQuery | null>(null)
  const [hasSearched, setHasSearched] = useState(false)

  const performSearch = useCallback(async (query: SearchQuery, page: number = 1) => {
    setLoading(true)
    setError(null)

    try {
      const searchQuery: SearchQuery = { ...query, page, pageSize: PAGE_SIZE }
      const response = await searchService.searchCOA(searchQuery)
      setResults(response.results)
      setTotalResults(response.total)
      setCurrentPage(page)
      setLastQuery(query)
      setHasSearched(true)
    } catch (err) {
      const errorMessage = sanitizeError(err)
      setError(errorMessage)
      setResults([])
      setTotalResults(0)
      setHasSearched(true)
    } finally {
      setLoading(false)
    }
  }, [])

  const handleSearch = useCallback((query: SearchQuery) => {
    performSearch(query, 1)
  }, [performSearch])

  const handlePageChange = useCallback((page: number) => {
    if (lastQuery) performSearch(lastQuery, page)
  }, [lastQuery, performSearch])

  const handleRetry = useCallback(() => {
    if (lastQuery) performSearch(lastQuery, currentPage)
    else setError(null)
  }, [lastQuery, currentPage, performSearch])

  const handleViewDocument = useCallback((result: SearchResult) => {
    console.log('View document:', result.id)
  }, [])

  return (
    <div className="search-coa-page">
      <div className="search-coa-page__container">
        <header className="search-coa-page__header">
          <h1>Certificate of Analysis Lookup</h1>
          <p className="search-coa-page__description">
            Search by your Accession Number, Search Code, or Company Name.
          </p>
        </header>

        <section className="search-coa-page__form-section" aria-label="Search form">
          <SearchForm onSearch={handleSearch} loading={loading} />
        </section>

        {error && (
          <section className="search-coa-page__error-section" aria-label="Error message">
            <ErrorMessage message={error} onRetry={handleRetry} className="search-coa-page__error" />
          </section>
        )}

        {hasSearched && (
          <section className="search-coa-page__results-section" aria-label="Search results">
            <SearchResults
              results={results}
              loading={loading}
              currentPage={currentPage}
              pageSize={PAGE_SIZE}
              total={totalResults}
              onPageChange={handlePageChange}
              onViewDocument={handleViewDocument}
            />
          </section>
        )}
      </div>
    </div>
  )
}

export default SearchCOAPage
